import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { Grid, FormControlLabel } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import { decipherError } from '../../../../../../utils/decipherError'
import SydSwitch from '../../../../../commonDesign/SydSwitch'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1)
  },
  infoText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  warningContainer: {
    marginTop: theme.spacing(2)
  },
  formControlLabel: {
    '& .MuiFormControlLabel-label': {
      paddingLeft: 'var(--padding-sm)'
    }
  }
}))

const useSubmitter = (dataSource, config, onComplete) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const mutationHook = config.mutationHook()

  const onSubmit = useCallback(async (formData) => {
    const command = {
      datasourceId: dataSource.catalogDatasourceId,
      [config.propertyName]: formData[config.propertyName]
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await mutationHook.mutateAsync(command)
      if (result?.statusCode === 500) {
        setError(config.errorMessage)
        return
      }

      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [dataSource, config, onComplete, mutationHook])

  return {
    onSubmit,
    processing,
    error
  }
}

function ModifyDatasourceFlagForm ({ dataSource, config, onCancel, onComplete }) {
  const classes = useStyles()
  const initialValue = dataSource?.[config.propertyName] || false
  const [value, setValue] = useState(initialValue)
  const { onSubmit, processing, error } = useSubmitter(dataSource, config, onComplete)

  if (!dataSource) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Alert severity='error'>No data source provided</Alert>
        </Grid>
      </Grid>
    )
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className={classes.infoText}>
            {config.infoText}
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.switchContainer}>
            <FormControlLabel
              className={classes.formControlLabel}
              control={
                <SydSwitch
                  value={value}
                  onChange={(e, newValue) => setValue(newValue)}
                  disabled={processing}
                />
              }
              label={config.getLabel(value)}
            />
          </div>
        </Grid>

        {config.warningAlert && value && (
          <Grid item xs={12}>
            <div className={classes.warningContainer}>
              {config.warningAlert}
            </div>
          </Grid>
        )}

        {/* Actions */}
        <Grid item xs={12}>
          <SydModalActions>
            {error && <div className='__error'>{error}</div>}
            <SydButton
              disabled={processing}
              variant='ghost'
              size='md'
              onClick={onCancel}
            >
              Cancel
            </SydButton>
            <SydButton
              processing={processing}
              variant='primary'
              size='md'
              onClick={() => onSubmit({ [config.propertyName]: value })}
            >
              Save Changes
            </SydButton>
          </SydModalActions>
        </Grid>
      </Grid>
    </div>
  )
}

ModifyDatasourceFlagForm.propTypes = {
  dataSource: PropTypes.shape({
    catalogDatasourceId: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired
  }).isRequired,
  config: PropTypes.shape({
    propertyName: PropTypes.string.isRequired,
    infoText: PropTypes.string.isRequired,
    getLabel: PropTypes.func.isRequired,
    errorMessage: PropTypes.string.isRequired,
    mutationHook: PropTypes.func.isRequired,
    warningAlert: PropTypes.node
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default ModifyDatasourceFlagForm
